import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Grid,
	Card,
	CardContent,
	Typography,
	Stack,
	Chip,
	Button,
	TextField,
	Alert,
	Avatar,
	Paper,
	Divider,
	Popover,
	Skeleton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import MainGrid from '../../components/MainGrid';
import { Cable, Description } from '@mui/icons-material';
import { getAccessToken, getElderHomeId } from '../../Utils/AuthUtil';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	fetch360DashboardData,
	fetchAllExternalElder,
	fetchResidents,
} from '../../services/ElderService';
import { toast } from 'react-toastify';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CableIcon from '@mui/icons-material/Cable';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import ClearIcon from '@mui/icons-material/Clear';
import { border, dark, surface, teal } from '../../assets/theme/Colors';

const statData = [
	{
		label: 'Total Residents',
		value: 0,
		sub: 'Total residents in system',
	},
	{
		label: 'Need Attention',
		value: 0,
		sub: 'No care plan',
	},
	{
		label: 'Plans Up to Date',
		value: 0,
		sub: 'All plans are current',
	},
	{
		label: 'Observations Today',
		value: 0,
		sub: 'via staff app',
	},
];

export const RESIDENT_STATUSES = [
	{
		text: 'Critical',
		color: '#8a2020',
		type: 'error',
	},
	{
		text: 'Attention',
		color: '#7c5200',
		type: 'warning',
	},
	{
		text: 'Stable',
		color: '#2d7a4a',
		type: 'success',
	},
];

const colors = [
	'#2563eb', // blue
	'#dc2626', // red
	'#00a89d', // green
	'#ca8a04', // yellow
	'#7c3aed', // purple
	'#ea580c', // orange
	'#0891b2', // cyan
	'#db2777', // pink
];

const tagColors = ['#7c5200', '#3c3489', '#6b8888', '#8a2020'];

const filterKeys = [
	'All Residents',
	'Critical',
	'Attention',
	'Overdue',
	'Stable',
];

function FilterChip({
	label,
	value,
	selectedValue,
	onSelect,
	color = 'primary',
	icon,
	disabled,
}) {
	const selected = value === selectedValue;

	return (
		<Chip
			label={label}
			clickable
			disabled={disabled}
			{...(icon && { icon })}
			color={selected ? color : 'default'}
			variant={selected ? 'filled' : 'outlined'}
			onClick={() => onSelect(value)}
			sx={{
				height: 29,
				fontSize: '12px',
				backgroundColor: selected ? dark : '#fff',
				color: selected ? '#fff' : '#6b8888',

				'& .MuiChip-icon': {
					color: selected ? '#fff' : 'inherit',
				},
			}}
		/>
	);
}

function DashboardFilterPanel({
	filters,
	onFilterChange,
	onClearFilters,
	resultText = 'Showing all residents',
}) {
	return (
		<Box
			sx={{
				minWidth: 280,
			}}>
			{/* Status */}
			<Box mb={2}>
				<Typography
					variant="caption"
					sx={{
						display: 'block',
						fontWeight: 700,
						textTransform: 'uppercase',
						letterSpacing: 0.6,
						mb: 1,
						fontSize: 10,
						color: 'text.secondary',
					}}>
					Status
				</Typography>

				<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
					<FilterChip
						label="All"
						value="all"
						selectedValue={filters.status}
						onSelect={value => onFilterChange('status', value)}
						disabled={false}
					/>

					<FilterChip
						label="Critical"
						value="critical"
						icon={
							<ErrorOutlineIcon
								sx={{
									width: 16,
									height: 16,
									color: 'warning.main',
								}}
							/>
						}
						selectedValue={filters.status}
						onSelect={value => onFilterChange('status', value)}
						disabled={true}
					/>

					<FilterChip
						label="Attention"
						value="attention"
						icon={
							<WarningAmberIcon sx={{ width: 16, height: 16 }} />
						}
						selectedValue={filters.status}
						onSelect={value => onFilterChange('status', value)}
						disabled={true}
					/>

					<FilterChip
						label="Stable"
						value="stable"
						icon={
							<CheckCircleOutlineIcon
								sx={{ width: 16, height: 16 }}
							/>
						}
						selectedValue={filters.status}
						onSelect={value => onFilterChange('status', value)}
						disabled={true}
					/>
				</Stack>
			</Box>

			{/* Source */}
			<Box mb={2}>
				<Typography
					variant="caption"
					sx={{
						display: 'block',
						fontWeight: 700,
						textTransform: 'uppercase',
						letterSpacing: 0.6,
						mb: 1,
						fontSize: 10,
						color: 'text.secondary',
					}}>
					Onboarding Source
				</Typography>

				<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
					<FilterChip
						label="All"
						value="all"
						selectedValue={filters.source}
						onSelect={value => onFilterChange('source', value)}
					/>

					<FilterChip
						label="API"
						value="api"
						icon={<CableIcon sx={{ width: 16, height: 16 }} />}
						selectedValue={filters.source}
						onSelect={value => onFilterChange('source', value)}
					/>

					<FilterChip
						label="Manual"
						value="manual"
						icon={<UploadFileIcon sx={{ width: 16, height: 16 }} />}
						selectedValue={filters.source}
						onSelect={value => onFilterChange('source', value)}
					/>
				</Stack>
			</Box>

			{/* Care Plan */}
			<Box mb={2}>
				<Typography
					variant="caption"
					sx={{
						display: 'block',
						fontWeight: 700,
						textTransform: 'uppercase',
						letterSpacing: 0.6,
						mb: 1,
						fontSize: 10,
						color: 'text.secondary',
					}}>
					Care Plan
				</Typography>

				<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
					<FilterChip
						label="All"
						value="all"
						selectedValue={filters.plan}
						onSelect={value => onFilterChange('plan', value)}
					/>

					<FilterChip
						label="Has Plan"
						value="has-plan"
						icon={
							<AssignmentTurnedInIcon
								sx={{ width: 16, height: 16 }}
							/>
						}
						selectedValue={filters.plan}
						onSelect={value => onFilterChange('plan', value)}
					/>

					<FilterChip
						label="No Plan"
						value="no-plan"
						icon={
							<AssignmentLateIcon
								sx={{ width: 16, height: 16 }}
							/>
						}
						selectedValue={filters.plan}
						onSelect={value => onFilterChange('plan', value)}
					/>
				</Stack>
			</Box>

			<Divider sx={{ mb: 1.5 }} />
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center">
				<Typography sx={{ color: '#6b8888', fontSize: 11 }}>
					{resultText}
				</Typography>

				<Button
					size="small"
					variant="text"
					startIcon={<ClearIcon />}
					onClick={onClearFilters}
					sx={{
						alignItems: 'center',
						padding: '5px 12px', // btn-sm
						borderRadius: 2,
						fontSize: '12px',
						textTransform: 'none',
						whiteSpace: 'nowrap',
						cursor: 'pointer',
						backgroundColor: '#F0F0F0',
						color: '#000000',
					}}>
					Clear
				</Button>
			</Stack>
		</Box>
	);
}

export default function ResidentDashboard360() {
	const location = useLocation();
	const [stats, setStats] = useState(statData);
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const navigate = useNavigate();
	const [resAttentionCount, setResAttentionCount] = useState(0);
	const [resPlanOptCount, setResPlanOptCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [residentBehaviorLevel, setResidentBehaviorLevel] = useState({});
	const [residentSummary, setResidentSummary] = useState([]);
	const [residentSummaryData, setResidentSummaryData] = useState([]);
	const [searchText, setSearchedText] = useState('');
	const [filterTags, setFilterTags] = useState(filterKeys);
	const [filter, setFilter] = useState(0);
	const [anchorEl, setAnchorEl] = useState(null);
	const [filters, setFilters] = useState({
		status: 'all',
		source: 'all',
		plan: 'all',
	});
	const [residentsWithPlan, setResidentsWithPlan] = useState([]);

	useEffect(() => {
		fetchDashboardData();
	}, []);

	const filteredTableData = useMemo(() => {
		return applyFilters(residentSummary);
	}, [residentSummary, filters]);

	async function fetchDashboardData() {
		setIsLoading(true);
		const response = await fetch360DashboardData(homeId, token);
		if (!response.error) {
			setResidentsWithPlan(response.elderWithPlanIds);

			const updates = {
				'Total Residents': response.totalResident,
				'Need Attention': response.noPlanResCount,
				'Plans Up to Date': response.planResCount,
				'Observations Today': response.careNoteCount,
			};

			const updatedState = statData.map(item => ({
				...item,
				value: updates[item.label] ?? item.value,
			}));

			const counts = {
				'All Residents': response.totalResident,
				Critical: 0,
				Attention: 0,
				Overdue: 0,
				Stable: 0,
			};

			const updatedFilters = filterKeys.map(key => {
				const count = counts[key] ?? 0;
				return `${key} (${count})`;
			});

			setFilterTags(updatedFilters);
			setStats(updatedState);
			setResidentBehaviorLevel(response.questionCategory);
		} else {
			toast.error(response.message);
			setIsLoading(false);
		}

		await fetchResident(response.elderWithPlanIds ?? []);
	}

	async function fetchResident(residentPlanId) {
		const response = await fetchResidents(homeId, token);
		const exResponse = await fetchAllExternalElder(homeId, token);

		const formatResidents = (data, isExternal) => {
			const filteredData = !isExternal
				? data
				: data.filter(item => item.reviewed);

			return filteredData.map((item, index) => {
				const hasPlan = residentPlanId?.includes(item.id);

				const initials = item.name
					?.split(' ')
					.map(word => word[0])
					.join('')
					.toUpperCase()
					.slice(0, 2);

				const avatarColor = colors[index % colors.length];

				return {
					id: item.id,
					elderId: item.elderId,
					name: item.name,
					initials,
					avatar: avatarColor,
					room:
						item.elderHistory?.roomNumber ||
						item.elderHistory?.RoomNumber ||
						'N/A',

					careType: item.elderHistory?.careType || 'N/A',
					source: isExternal ? 'API' : 'Manual',
					plan: 'Current',
					lastObs: 'N/A',
					risk: 'Low',
					riskColor: 'green',
					planColor: 'green',
					isExternal,
					tags: item.tags,
					createdDate: item.createdDate,
					hasPlan,
				};
			});
		};

		let allResidents = [];

		if (response.message === 'Success') {
			allResidents = [...formatResidents(response.content || [], false)];
		}

		if (exResponse.message === 'Success') {
			allResidents = [
				...allResidents,
				...formatResidents(exResponse.content || [], true),
			];
		}

		const sortedResidents = [...allResidents].sort((a, b) => {
			const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
			const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;

			return dateB - dateA; // newest first
		});

		setResidentSummary(sortedResidents);
		setResidentSummaryData(sortedResidents);
		setIsLoading(false);
	}

	function covertArrayToBulletString(array) {
		if (array.length === 0) {
			return 'No latest reviews';
		}
		return array.map(item => `• ${item}`).join('\n');
	}

	async function onOptimizePlan(
		elderId,
		elderName,
		id,
		exPlanId,
		isExternalElder,
		hasPlan,
	) {
		if (hasPlan) {
			navigate(`/carePlan/${id}`, {
				state: {
					elderId,
					id,
					elderName,
					exPlanId: exPlanId && exPlanId !== '' ? exPlanId : null,
					isExternalElder,
					fromDashboard: true,
				},
			});
		} else {
			navigate(`/initialCarePlan`);
		}
	}

	function onViewProfile(resident) {
		navigate(`/resident/${resident.id}`, {
			state: {
				id: resident.id,
				elderId: resident.elderId,
				isExternalElder: resident.isExternal,
				from: location.pathname,
			},
		});
	}

	const getStatusFromTags = (tags = []) => {
		if (!tags || tags.length === 0) {
			return {
				text: 'N/A',
				color: '#6b8888',
				type: 'default',
			};
		}

		const match = tags
			.map(tag =>
				RESIDENT_STATUSES.find(
					status => status.text.toLowerCase() === tag.toLowerCase(),
				),
			)
			.find(Boolean);

		return (
			match || {
				text: 'N/A',
				color: '#6b8888',
				type: 'default',
			}
		);
	};

	function onSearch(search) {
		setSearchedText(search);
		const searchText = search.toLowerCase();

		if (search === '') {
			setResidentSummary(residentSummaryData);
		} else {
			const searchResults = residentSummaryData.filter(value => {
				return (
					value.elderId.toLowerCase().includes(searchText) ||
					value.name.toLowerCase().includes(searchText)
				);
			});

			if (searchResults.length > 0) {
				setResidentSummary(searchResults);
			} else {
				setResidentSummary([]);
			}
		}
	}

	const handleOpen = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleFilterChange = (type, value) => {
		setFilters(prev => ({
			...prev,
			[type]: value,
		}));
	};

	function applyFilters(data) {
		const planIdSet = new Set(residentsWithPlan);
		return data.filter(item => {
			const statusOk =
				filters.status === 'all' ||
				item.tags?.some(
					tag => tag.toLowerCase() === filters.status.toLowerCase(),
				);

			const hasPlan = planIdSet.has(item.id);

			const planOk =
				filters.plan === 'all' ||
				(filters.plan === 'has-plan' && hasPlan) ||
				(filters.plan === 'no-plan' && !hasPlan);

			const sourceOk =
				filters.source === 'all' ||
				(filters.source === 'api' && item.isExternal === true) ||
				(filters.source === 'manual' && item.isExternal === false);

			return statusOk && planOk && sourceOk;
		});
	}

	return (
		<MainGrid>
			<Box sx={{ width: '100%' }}>
				{/* HEADER */}
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					mb={3}>
					<Box>
						<Typography
							sx={{
								fontSize: '22px',
								fontWeight: 600,
								color: dark,
								mb: 0.5,
							}}>
							Resident 360 Dashboard
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Person-centred overview of all residents —
							attention-first, action-ready
						</Typography>
					</Box>

					<Stack direction="row" spacing={1}>
						<TextField
							size="small"
							placeholder="Search resident..."
							onChange={e => onSearch(e.target.value)}
							InputProps={{
								sx: {
									height: 30,
									fontSize: 12,
									backgroundColor: '#FFFFFF',
									'& input': {
										padding: '4px 8px',
									},
								},
							}}
						/>
						<>
							<Button
								onClick={handleOpen}
								variant="outlined"
								sx={{
									background: '#FFFFFF',
									color: dark,
									fontWeight: 600,
									borderRadius: 2,
									height: 30,
									fontSize: 12,
									textTransform: 'none',
									px: 2,
									'&:hover': {
										background: '#FFFFFF',
									},
								}}
								startIcon={<FilterListIcon />}>
								Filter
							</Button>
							<Popover
								open={Boolean(anchorEl)}
								anchorEl={anchorEl}
								onClose={handleClose}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'right',
								}}
								transformOrigin={{
									vertical: 'top',
									horizontal: 'right',
								}}
								slotProps={{
									paper: {
										sx: {
											mt: 0.75, // ~6px gap below button
											minWidth: 280,
											p: 2,
											borderRadius: 2,
										},
									},
								}}>
								<DashboardFilterPanel
									filters={filters}
									onFilterChange={handleFilterChange}
									onClearFilters={() =>
										setFilters({
											status: 'all',
											source: 'all',
											plan: 'all',
										})
									}
								/>
							</Popover>
						</>
						<Button
							variant="contained"
							onClick={() => navigate('/addResident')}
							startIcon={<PersonAddIcon />}
							sx={{
								background: teal,
								color: dark,
								fontWeight: 600,
								borderRadius: 2,
								height: 30,
								fontSize: 12,
								textTransform: 'none',
								px: 2,
								'&:hover': {
									background: '#00b0a0',
								},
							}}>
							Onboard
						</Button>
					</Stack>
				</Stack>

				{/* ALERTS */}
				<Stack spacing={1.5} mb={3}>
					{resAttentionCount > 0 && (
						<Alert severity="error" sx={{ fontSize: '13px' }}>
							<b>
								{resAttentionCount} residents need immediate
								attention
							</b>
						</Alert>
					)}

					{resPlanOptCount > 0 && (
						<Alert severity="warning" sx={{ fontSize: '13px' }}>
							<b>
								{resPlanOptCount} care plan optimizations
								pending
							</b>{' '}
							- New observations available for review.
						</Alert>
					)}
				</Stack>

				{/* STATS */}
				<Grid container spacing={2} mb={3}>
					{stats.map((s, i) => (
						<Grid item xs={12} md={3} key={i}>
							<Card
								elevation={0}
								sx={{
									borderRadius: 2,
									backgroundColor:
										i === 0 ? '#e6f9f7' : '#FFFFFF',
									border: `1px solid ${i === 0 ? '#b3ede8' : border}`,
								}}>
								<CardContent>
									<Typography
										fontSize={'11px'}
										color="text.secondary">
										{s.label}
									</Typography>
									{isLoading ? (
										<Skeleton
											variant="text"
											width={16}
											height={42}
										/>
									) : (
										<Typography
											fontSize={'26px'}
											fontWeight={'700'}>
											{s.value}
										</Typography>
									)}
									<Typography
										fontSize={'12px'}
										color="text.secondary">
										{s.sub}
									</Typography>
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>

				{/* FILTER PILLS */}
				{/* <Stack direction="row" spacing={1} flexWrap="wrap" mb={3}>
					{filterTags.map((f, i) => (
						<Chip
							key={i}
							label={f}
							clickable
							color={i === filter ? 'primary' : 'default'}
							disabled
							onClick={() => setFilter(i)}
							variant={i === filter ? 'filled' : 'outlined'}
							sx={{
								backgroundColor: i === filter ? dark : '#fff',
								height: 29,
								fontSize: '12px',
								color: i === filter ? '#fff' : '#6b8888',
							}}
						/>
					))}
				</Stack> */}

				<Grid container spacing={2}>
					{filteredTableData.length > 0
						? filteredTableData.map((resident, index) => {
								const status = getStatusFromTags(resident.tags);
								return (
									<Grid item xs={12} md={4} key={index}>
										<Card
											// onClick={() => showScreen('residentProfile')}
											sx={{
												border: '1px solid #d8e8e8',
												borderLeft: `3px solid ${resident.borderColor}`,
												borderRadius: '12px',
												cursor: 'pointer',
												transition: 'all 0.15s ease',
												'&:hover': {
													borderColor: teal,
													boxShadow:
														'0 4px 16px rgba(0,196,180,0.12)',
													transform:
														'translateY(-1px)',
												},
											}}>
											<CardContent sx={{ p: 2 }}>
												{/* HEADER */}
												<Stack
													direction="row"
													spacing={1.5}
													alignItems="flex-start"
													justifyContent="space-between"
													mb={1.5}>
													<Stack
														direction="row"
														spacing={1.5}>
														<Avatar
															sx={{
																width: 38,
																height: 38,
																fontSize: 13,
																fontWeight: 600,
																bgcolor:
																	colors[
																		index %
																			colors.length
																	],
															}}>
															{resident.name
																.split(' ')
																.map(
																	word =>
																		word[0],
																)
																.join('')
																.toUpperCase()
																.slice(0, 2)}
														</Avatar>

														<Box>
															<Typography
																sx={{
																	fontSize: 14,
																	fontWeight: 600,
																	color: dark,
																}}>
																{resident.name}
															</Typography>

															<Typography
																sx={{
																	fontSize: 11,
																	color: '#6b8888',
																}}>
																{resident.isExternal
																	? 'Api Onboarded'
																	: 'Manually Onboarded'}
															</Typography>
														</Box>
													</Stack>

													<Chip
														label={status.text}
														size="small"
														sx={{
															backgroundColor: `${status.color}20`,
															color: status.color,
															fontSize: 11,
														}}
													/>
												</Stack>

												{/* TAGS */}
												<Stack
													direction="row"
													spacing={0.75}
													flexWrap="wrap"
													useFlexGap
													mb={1.5}>
													{resident.tags &&
														resident.tags.map(
															(tag, tIndex) => {
																const color =
																	tagColors[
																		tIndex %
																			tagColors.length
																	];
																return (
																	<Chip
																		key={
																			tIndex
																		}
																		label={
																			tag
																		}
																		size="small"
																		sx={{
																			backgroundColor: `${color}20`,
																			color: color,
																			fontSize: 11,
																		}}
																	/>
																);
															},
														)}
												</Stack>

												{/* FOOTER */}
												<Stack
													direction="row"
													justifyContent="space-between"
													alignItems="center"
													pt={1.25}
													sx={{
														borderTop:
															'1px solid #d8e8e8',
													}}>
													<Stack
														direction="row"
														spacing={1}>
														<Button
															onClick={e => {
																e.stopPropagation();
																onOptimizePlan(
																	resident.id,
																	resident.name,
																	resident.elderId,
																	resident.externalCarePlanId,
																	resident.isExternalElder,
																	resident.hasPlan,
																);
															}}
															size="small"
															variant={
																'contained'
															}
															startIcon={
																<RefreshIcon fontSize="small" />
															}
															sx={{
																textTransform:
																	'none',
																borderRadius:
																	'8px',
																fontSize: 12,
																minWidth:
																	'auto',
																boxShadow:
																	'none',
																bgcolor: teal,
																color: dark,
																fontWeight: 600,
																'&:hover': {
																	bgcolor:
																		'#00b0a0',
																	boxShadow:
																		'none',
																},
															}}>
															{!resident.hasPlan
																? 'Create Plan'
																: 'Optimize Plan'}
														</Button>
														<Button
															onClick={e => {
																e.stopPropagation();
																onViewProfile(
																	resident,
																);
															}}
															size="small"
															variant={'outlined'}
															sx={{
																textTransform:
																	'none',
																borderRadius:
																	'8px',
																fontSize: 12,
																minWidth:
																	'auto',
																boxShadow:
																	'none',
																fontWeight: 600,
																'&:hover': {
																	boxShadow:
																		'none',
																},
															}}>
															View Profile
														</Button>
													</Stack>
												</Stack>
											</CardContent>
										</Card>
									</Grid>
								);
							})
						: isLoading
							? Array.from({ length: 3 }).map((_, index) => (
									<Grid item xs={12} md={4} key={index}>
										<Skeleton
											variant="rectangular"
											height={140}
											sx={{
												borderRadius: 2,
											}}
										/>
									</Grid>
								))
							: null}

					<Grid item xs={12} md={4}>
						<Card
							sx={{
								border: '1.5px dashed #d8e8e8',
								borderRadius: '12px',
								cursor: 'pointer',
								transition: 'all 0.15s ease',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								textAlign: 'center',
								p: 3,
								'&:hover': {
									borderColor: teal,
									boxShadow:
										'0 4px 16px rgba(0,196,180,0.12)',
									transform: 'translateY(-1px)',
									backgroundColor: surface,
								},
							}}>
							<CardContent
								sx={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									p: '0 !important',
								}}>
								<Avatar
									sx={{
										width: 44,
										height: 44,
										bgcolor: '#6b8888',
										mb: 1.5,
									}}>
									<PersonAddIcon sx={{ fontSize: 20 }} />
								</Avatar>

								<Typography
									sx={{
										fontSize: 14,
										fontWeight: 600,
										color: '#6b8888',
										mb: 0.5,
									}}>
									Onboard New Resident
								</Typography>

								{/* <Typography
									sx={{
										fontSize: 12,
										color: '#6b8888',
										mb: 1.5,
									}}>
									3 pending from Care Vision API
								</Typography> */}

								<Button
									variant="contained"
									onClick={() => navigate('/settings')}
									size="small"
									startIcon={<Cable />}
									sx={{
										backgroundColor: teal,
										color: dark,
										fontWeight: 600,
										textTransform: 'none',
										borderRadius: '8px',
										boxShadow: 'none',
										px: 2,
										'&:hover': {
											backgroundColor: '#00b0a0',
											boxShadow: 'none',
										},
									}}>
									Import from API
								</Button>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>
		</MainGrid>
	);
}
